import React, { useRef } from "react";
import { TableRowData } from "./data-table";
import SidePanel, { PanelHeader } from "./side-panel";
import ScaledPreview from "./scaled-preview";
import { useIsMobile } from "../hooks/use-is-mobile";
import { ScrollArea } from "./ui/scroll-area";
import { Icons } from "./ui/icons";
import { MovementActivity } from "../lib/api";
import { format } from "date-fns";

const BLANK = "_______________________________";
const BLANK_ITEM_ROWS = 15;

interface ReturningNoteDrawerProps {
  movementData: MovementActivity | undefined;
  returningNoteDetails: TableRowData | null;
  blank?: boolean;
  onClose: () => void;
}

const ReturningNoteDrawer: React.FC<ReturningNoteDrawerProps> = ({
  movementData,
  returningNoteDetails,
  blank = false,
  onClose,
}) => {
  const transferNo = blank
    ? BLANK
    : movementData?.reference
      ? movementData.reference
      : "N/A";
  const printRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const sourceName = blank
    ? BLANK
    : movementData?.sourceSite
      ? [
          movementData.sourceSite.customer?.fullName,
          movementData.sourceSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : movementData?.sourceWarehouse?.name ||
        movementData?.sourceVendor?.fullName ||
        "N/A";

  const destinationName = blank
    ? BLANK
    : movementData?.destinationSite
      ? [
          movementData.destinationSite.customer?.fullName,
          movementData.destinationSite.name,
        ]
          .filter(Boolean)
          .join(" - ")
      : movementData?.destinationWarehouse?.name ||
        movementData?.destinationCustomer?.fullName ||
        "N/A";

  const displayDate = blank
    ? BLANK
    : movementData?.plannedDate
      ? format(new Date(movementData.plannedDate), "MMM dd, yyyy")
      : movementData?.createdAt
        ? format(new Date(movementData.createdAt), "MMM dd, yyyy")
        : "N/A";

  const driverName = blank
    ? BLANK
    : (movementData?.driverName ?? movementData?.driver_name ?? BLANK);
  const driverPhone = blank
    ? BLANK
    : (movementData?.driverPhoneNumber ??
      movementData?.driver_phone_number ??
      BLANK);
  const vehicleNo = blank
    ? BLANK
    : (movementData?.vehicleNumber ?? movementData?.vehicle_number ?? BLANK);

  const handlePrint = () => {
    if (!printRef.current) return;
    const el = printRef.current;
    const parent = el.parentNode;
    const next = el.nextSibling;
    const originalTitle = document.title;

    document.body.appendChild(el);
    document.title = blank
      ? "Blank Returning Note"
      : "Returning Note From " + sourceName;
    window.print();

    document.title = originalTitle;
    if (parent) parent.insertBefore(el, next);
    else el.remove();
  };

  return (
    <SidePanel
      open={!!returningNoteDetails}
      onClose={onClose}
      width="w-[900px]"
      layer="overlay"
    >
      {returningNoteDetails && (
        <div className="relative h-full flex flex-col">
          <PanelHeader
            title={
              blank
                ? "Blank Returning Note"
                : `Returning Note from ${sourceName} to ${destinationName}`
            }
            onClose={onClose}
            actions={[
              {
                label: "Print",
                icon: Icons.Print,
                onClick: handlePrint,
                pinned: true,
              },
            ]}
          />
          <div className="flex-grow overflow-hidden">
            <ScrollArea className="h-full">
              <div
                className={
                  isMobile ? "w-full p-3 bg-gray-50" : "w-full p-6 bg-gray-50"
                }
              >
                <ScaledPreview>
                  <div
                    ref={printRef}
                    className="print-area p-4 bg-white shadow-lg rounded-lg"
                  >
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-gray-950 mb-2 border-b-2 border-gray-950 inline-block mx-auto">
                        RETURNING NOTE
                      </h1>
                    </div>
                    <p className="text-gray-950 text-right">No: {transferNo}</p>
                    <div className="text-center mb-4 border-b-2 border-gray-950 pb-2">
                      <img
                        src="/images/logo.png"
                        alt="Logo"
                        className="h-12 mx-auto mb-2"
                      />
                      <p className="text-sm text-gray-950">
                        Dealers in scaffolding & formwork (sale, rental and
                        fixing, cradle & scissor lift rental and sale)
                      </p>
                      <p className="text-sm text-gray-950">
                        Office: Plot 161, Block 161, Wazo, Tegeta
                      </p>
                      <p className="text-sm text-gray-950">
                        P.O Box 35854, Dar es Salaam, Tanzania. Sales:
                        +255(0)780 973 724 Warehouse: +255(0)780 973 728
                      </p>
                      <p className="text-sm text-gray-950">
                        Email: info@waposcaffolding.com Website:
                        www.waposcaffolding.co.tz
                      </p>
                    </div>
                    <div className="mb-4">
                      <p className="text-gray-950">
                        <strong>From:</strong> {sourceName}
                      </p>
                      <p className="text-gray-950">
                        <strong>Return To:</strong> {destinationName}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 mb-4 border-2 border-gray-950 p-3 rounded-2xl">
                      <div>
                        <p className="text-gray-950">
                          <strong>Driver Name:</strong> {driverName}
                        </p>
                        <p className="text-gray-950">
                          <strong>Driver Phone No:</strong> {driverPhone}
                        </p>
                        <p className="text-gray-950">
                          <strong>Vehicle No:</strong> {vehicleNo}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-950">
                          <strong>Date:</strong> {displayDate}
                        </p>
                        <p className="text-gray-950">
                          <strong>TIN:</strong> 124-154-243
                        </p>
                        <p className="text-gray-950">
                          <strong>VRN:</strong> 40-028-187-U
                        </p>
                      </div>
                    </div>
                    <table className="w-full border-collapse mb-4">
                      <thead>
                        <tr className="bg-gray-950 border border-gray-950">
                          <th className="text-white p-2">Description</th>
                          <th className="text-white p-2">Qty</th>
                          <th className="text-white p-2">Damaged Qty</th>
                          <th className="text-white p-2">Unit</th>
                          <th className="text-white p-2">Reason for Return</th>
                        </tr>
                      </thead>
                      <tbody>
                        {blank
                          ? Array.from({ length: BLANK_ITEM_ROWS }).map(
                              (_, index) => (
                                <tr key={index}>
                                  <td className="border border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                  <td className="border border-gray-950 px-2 h-8">
                                    &nbsp;
                                  </td>
                                </tr>
                              ),
                            )
                          : Array.isArray(movementData?.items) &&
                            movementData?.items?.map((item, index) => (
                              <tr key={item.id || index}>
                                <td className="border border-gray-950 px-2">
                                  <p className="text-[10px] text-gray-950">
                                    {[
                                      item?.particulars ||
                                        item?.inventory?.description ||
                                        "N/A",
                                      item?.inventory?.length === "undefined" ||
                                      item?.inventory?.length == null
                                        ? "---"
                                        : item.inventory.length,
                                    ]
                                      .filter(Boolean)
                                      .join(" • ")}
                                  </p>
                                </td>
                                <td className="border border-gray-950 px-2 text-center">
                                  <p className="text-[10px] text-gray-950">
                                    {item?.totalQuantity || 0}
                                  </p>
                                </td>
                                <td className="border border-gray-950 px-2 text-center">
                                  <p className="text-[10px] text-gray-950">
                                    {item?.damagedQuantity || 0}
                                  </p>
                                </td>
                                <td className="border border-gray-950 px-2 text-center">
                                  <p className="text-[10px] text-gray-950">
                                    {item?.inventory?.unitWeight || "N/A"}
                                  </p>
                                </td>
                                <td className="border border-gray-950 px-2">
                                  <p className="text-[10px] text-gray-950">
                                    {item?.reason?.trim() ?? "—"}
                                  </p>
                                </td>
                              </tr>
                            ))}
                      </tbody>
                    </table>
                    {!blank && movementData?.notes?.trim() && (
                      <p className="text-gray-950 text-sm mb-4">
                        <strong>Notes:</strong> {movementData.notes.trim()}
                      </p>
                    )}
                    <div className="flex justify-between">
                      <div>
                        <p className="text-gray-950">
                          <strong>Returned By:</strong>{" "}
                          {blank
                            ? BLANK
                            : (movementData?.returnedBy ??
                              movementData?.returned_by ??
                              BLANK)}
                        </p>
                        <p className="text-gray-950">
                          <strong>Received By:</strong>{" "}
                          {blank
                            ? BLANK
                            : (movementData?.receivedBy ??
                              movementData?.received_by ??
                              BLANK)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-950">
                          <strong>Date</strong>{" "}
                          {blank
                            ? BLANK
                            : movementData?.returnedDate
                              ? format(
                                  new Date(movementData.returnedDate),
                                  "MMM dd, yyyy",
                                )
                              : BLANK}
                        </p>
                        <p className="text-gray-950">
                          <strong>Date</strong>{" "}
                          {displayDate === "N/A" ? BLANK : displayDate}
                        </p>
                      </div>
                    </div>
                  </div>
                </ScaledPreview>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}
    </SidePanel>
  );
};

export default ReturningNoteDrawer;
